/**
 * useDashboardWidgets — small hook that owns the Today-tab widget
 * visibility list. Reads from /api/preferences/dashboard-widgets,
 * caches in localStorage for instant first paint, and exposes
 * `toggleWidget` / `saveWidgets` so the Customize modal can persist.
 *
 * Tier gating is enforced both server-side (PUT returns 403 if over the
 * limit) and client-side (so the UI disables checkboxes once you hit
 * your cap on Free tier).
 */
import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORAGE_KEY = 'isp_dashboard_widgets_v1';

const DEFAULT_STATE = {
  enabled: ['next-action', 'cards-due', 'habit-loop', 'semester-plan'],
  available: [
    'habit-loop', 'semester-plan', 'cards-due', 'next-action',
    'focus-cta', 'goal-progress', 'recent-lectures', 'upcoming-exams',
  ],
  limit: 4,
  tier: 'free',
  loading: true,
};

const readCache = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return { ...DEFAULT_STATE, ...JSON.parse(raw), loading: false };
  } catch { return null; }
};

const writeCache = (state) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({
      enabled: state.enabled, available: state.available,
      limit: state.limit, tier: state.tier,
    }));
  } catch { /* ignore */ }
};

export const useDashboardWidgets = () => {
  const [state, setState] = useState(() => readCache() || DEFAULT_STATE);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      // Anonymous users (no auth token) get defaults — no network call.
      if (!localStorage.getItem('auth_token')) {
        setState((s) => ({ ...s, loading: false }));
        return;
      }
      try {
        const { data } = await axios.get(`${API}/preferences/dashboard-widgets`);
        if (cancelled) return;
        const next = { ...data, loading: false };
        setState(next);
        writeCache(next);
      } catch {
        if (!cancelled) setState((s) => ({ ...s, loading: false }));
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const isEnabled = useCallback((id) => state.enabled.includes(id), [state.enabled]);

  const saveWidgets = useCallback(async (newEnabled) => {
    try {
      const { data } = await axios.put(`${API}/preferences/dashboard-widgets`, { enabled: newEnabled });
      const next = { ...state, ...data, loading: false };
      setState(next);
      writeCache(next);
      toast.success('Dashboard saved');
      return true;
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Could not save dashboard layout';
      toast.error(detail);
      return false;
    }
  }, [state]);

  return { ...state, isEnabled, saveWidgets };
};

export const WIDGET_META = {
  'habit-loop':     { label: 'First-week Habit Loop', hint: '7-day onboarding grid (auto-hides after week 1).' },
  'semester-plan':  { label: 'Semester Plan',         hint: "Today's tasks pulled from your semester schedule." },
  'cards-due':      { label: 'Cards Due Today',       hint: 'Spaced-repetition flashcards waiting for review.' },
  'next-action':    { label: 'Next Action',           hint: 'Focused 15-minute "do this next" hero card.' },
  'focus-cta':      { label: 'Focus Session CTA',     hint: 'One-tap launcher for a 25-min Pomodoro.' },
  'goal-progress':  { label: 'Goal Progress',         hint: 'Countdown to your saved goal (if set).' },
  'recent-lectures':{ label: 'Recent Lectures',       hint: 'Your last 5 recordings with key points.' },
  'upcoming-exams': { label: 'Upcoming Exams',        hint: "Next 5 scheduled exams from your plan." },
};
